import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import queueTask from 'post-task';
import { TwoFAWidget } from '@sbt-web/2fa';
import '@sbt-web/2fa/style';
import { getPublicUser } from '@sbt-web/auth';
import { postComplaint } from '@sbt-web/network/client';
import { HTTPStatusCode, NetError, type AdItem } from '@sbt-web/network/types';
import { Button, FullModal, Icon, IconSize } from '@sbt-web/ui';
import { AdAbuseForm } from '@/client/components/Detail/AdAbuse/components/Form';
import { FormError } from '@/client/components/Detail/AdAbuse/components/Form/FormError';
import { FormSuccess } from '@/client/components/Detail/AdAbuse/components/Form/FormSuccess';
import Labels from '@/client/components/Detail/AdAbuse/Labels';
import { adAbuseIntentionClick, adAbuseSendClick } from '@/client/components/Detail/AdAbuse/tracking';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import classes from './AdAbuse.module.scss';
enum State {
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  DONE = 'DONE',
  VERIFY = 'VERIFY',
}
export type Props = {
  item: AdItem;
};
export type FormValidation = {
  isReasonValid: boolean;
  isDescriptionValid: boolean;
  isClauseValid: boolean;
  isEmailValid?: boolean;
};
export const AdAbuse = t0 => {
  const $ = _c(67);
  const {
    item
  } = t0;
  const [opened, setOpened] = useState(false);
  const [state, setState] = useState(undefined);
  const [isAnonymous, setIsAnonymous] = useState(true);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (getPublicUser() !== null) {
        setIsAnonymous(false);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[2] !== isAnonymous || $[3] !== opened) {
    t3 = () => {
      if (opened && isAnonymous && getPublicUser() !== null) {
        setIsAnonymous(false);
      }
    };
    t4 = [opened, isAnonymous];
    $[2] = isAnonymous;
    $[3] = opened;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const [selectValue, setSelectValue] = useState(undefined);
  const [textAreaValue, setTextAreaValue] = useState("");
  const [clauseValue, setClauseValue] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [token, setToken] = useState(undefined);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      isReasonValid: true,
      isDescriptionValid: true,
      isClauseValid: true,
      isEmailValid: true
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const [formValidation, setFormValidation] = useState(t5);
  let t6;
  if ($[7] !== item) {
    t6 = function () {
      setOpened(true);
      queueTask(() => {
        adAbuseIntentionClick(item);
      }, "background");
    };
    $[7] = item;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const openDialog = t6;
  let T0;
  let handleWidgetError;
  let submitReport;
  let t10;
  let t7;
  let t8;
  let t9;
  if ($[9] !== clauseValue || $[10] !== emailValue || $[11] !== isAnonymous || $[12] !== item || $[13] !== opened || $[14] !== selectValue || $[15] !== state || $[16] !== textAreaValue || $[17] !== token) {
    submitReport = async () => {
      if (validateForm()) {
        setState(State.LOADING);
        ;
        try {
          await postComplaint(process.env.NEXT_PUBLIC_HADES_BASE_URL, item.urn, {
            clause: clauseValue,
            description: textAreaValue,
            reason: selectValue as string
          }, emailValue, token);
          setState(State.DONE);
          setToken(undefined);
        } catch (t11) {
          const e = t11;
          if (e instanceof NetError && e.cause.status === HTTPStatusCode.Forbidden) {
            const errors = (e.cause.errors ?? []) as Array<{
              error_code: string;
              detail: {
                session_token: string;
              };
            }>;
            const secondFactorError = errors.find(_temp);
            const authToken = secondFactorError?.detail?.session_token;
            if (authToken !== undefined) {
              setToken(authToken);
              setState(State.VERIFY);
              return;
            }
          }
          setState(State.ERROR);
          setToken(undefined);
        }
      }
    };
    let t12;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = () => {
        setState(State.ERROR);
        setToken(undefined);
      };
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    handleWidgetError = t12;
    const getActions = () => [state === State.DONE && <Button aria-label={Labels.FORM.success.button} key="success" design="solid" onClick={() => setOpened(false)}>{Labels.FORM.success.button}</Button>, state === State.ERROR && <Button aria-label={Labels.FORM.error.button} key="retry" design="solid" onClick={() => setState(undefined)}>{Labels.FORM.error.button}</Button>, state === undefined && <Button aria-label={Labels.FORM.button} key="submit" design="solid" onClick={() => {
      submitReport();
      queueTask(() => {
        adAbuseSendClick(item);
      }, "background");
    }}>{Labels.FORM.button}</Button>];
    const validateForm = () => {
      const validation = {
        isReasonValid: selectValue !== undefined,
        isDescriptionValid: textAreaValue !== "",
        isClauseValid: clauseValue,
        isEmailValid: isAnonymous ? emailValue !== "" : true
      };
      setFormValidation(validation);
      return Object.values(validation).every(_temp2);
    };
    T0 = FullModal;
    t7 = Labels.FORM.title;
    t8 = opened;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = () => setOpened(false);
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    t10 = getActions();
    $[9] = clauseValue;
    $[10] = emailValue;
    $[11] = isAnonymous;
    $[12] = item;
    $[13] = opened;
    $[14] = selectValue;
    $[15] = state;
    $[16] = textAreaValue;
    $[17] = token;
    $[18] = T0;
    $[19] = handleWidgetError;
    $[20] = submitReport;
    $[21] = t10;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    T0 = $[18];
    handleWidgetError = $[19];
    submitReport = $[20];
    t10 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t11;
  if ($[27] !== clauseValue || $[28] !== emailValue || $[29] !== formValidation || $[30] !== isAnonymous || $[31] !== item || $[32] !== selectValue || $[33] !== state || $[34] !== textAreaValue) {
    t11 = state === undefined ? <AdAbuseForm item={item} formValidation={formValidation} setTextAreaValue={setTextAreaValue} setClauseValue={setClauseValue} setSelectValue={setSelectValue} setEmailValue={setEmailValue} selectValue={selectValue} textAreaValue={textAreaValue} clauseValue={clauseValue} emailValue={emailValue} isAnonymous={isAnonymous} /> : null;
    $[27] = clauseValue;
    $[28] = emailValue;
    $[29] = formValidation;
    $[30] = isAnonymous;
    $[31] = item;
    $[32] = selectValue;
    $[33] = state;
    $[34] = textAreaValue;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== state) {
    t12 = state === State.LOADING ? <div role="presentation" aria-label="Attendi" id={classes.loader} /> : null;
    $[36] = state;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== handleWidgetError || $[39] !== state || $[40] !== submitReport || $[41] !== token) {
    t13 = state === State.VERIFY && token !== undefined ? <TwoFAWidget hadesPublicBase={process.env.NEXT_PUBLIC_HADES_BASE_URL} onError={handleWidgetError} onSuccess={submitReport} pulseInstance={getOrCreatePulse()} token={token} source="ad-abuse" layout="MODAL" /> : null;
    $[38] = handleWidgetError;
    $[39] = state;
    $[40] = submitReport;
    $[41] = token;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== state) {
    t14 = state === State.DONE ? <FormSuccess /> : null;
    $[43] = state;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== state) {
    t15 = state === State.ERROR ? <FormError /> : null;
    $[45] = state;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== T0 || $[48] !== t10 || $[49] !== t11 || $[50] !== t12 || $[51] !== t13 || $[52] !== t14 || $[53] !== t15 || $[54] !== t7 || $[55] !== t8 || $[56] !== t9) {
    t16 = <T0 title={t7} open={t8} onClose={t9} actions={t10} size="medium">{t11}{t12}{t13}{t14}{t15}</T0>;
    $[47] = T0;
    $[48] = t10;
    $[49] = t11;
    $[50] = t12;
    $[51] = t13;
    $[52] = t14;
    $[53] = t15;
    $[54] = t7;
    $[55] = t8;
    $[56] = t9;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = [classes["ad-abuse"]];
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  let t18;
  let t19;
  let t20;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Icon name="TriangleExclamation" iconSize={IconSize.SM} />;
    t19 = <span className={classNames("button-text", classes["ad-button-label"], classes.desktop)}>{Labels.BUTTON_LABEL}</span>;
    t20 = <span className={classNames("button-text", classes["ad-button-label"], classes.mobile)}>{Labels.BUTTON_LABEL_SHORT}</span>;
    $[59] = t18;
    $[60] = t19;
    $[61] = t20;
  } else {
    t18 = $[59];
    t19 = $[60];
    t20 = $[61];
  }
  let t21;
  if ($[62] !== openDialog) {
    t21 = <Button design="text" onClick={openDialog} classes={t17} aria-label={Labels.BUTTON_LABEL} icon={t18}>{t19}{t20}</Button>;
    $[62] = openDialog;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== t16 || $[65] !== t21) {
    t22 = <>{t16}{t21}</>;
    $[64] = t16;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  return t22;
};
function _temp(e_0) {
  return e_0.error_code === "AUTH:2fa-required";
}
function _temp2(valid) {
  return valid;
}